import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { blogPostsQ as postsQ, blogServicesQ as servicesQ, blogStatsQ as statsQ, consultPrices } from "@/features/blog/queries";
import { RouteNotFound } from "@/components/route-boundaries";
import { canonicalLink, canonicalMeta, ogImageMeta } from "@/lib/seo";
import { AGE_CHIPS, chipById, ltr, type AgeChipId } from "@/lib/blog/age-bands";
import { PILLARS, pillarById, postInPillar } from "@/lib/blog/pillars";
import { orderPath } from "@/lib/blog/paths";
import { articlesPhrase } from "@/lib/blog/text";
import { useReader } from "@/features/blog/use-reader";
import { Section } from "@/components/ds/section";
import { BrownCtaBand, PostCard, eyebrow, primaryBtn, secondaryBtn, type BlogPost } from "@/components/blog/shared";
import { PathProgress, PathView } from "@/components/blog/path-view";

const searchSchema = z.object({ rakiza: fallback(z.string().optional(), undefined) });

export const Route = createFileRoute("/_public/blog/")({
  validateSearch: zodValidator(searchSchema),
  head: () => ({
    meta: [
      { title: "المدوّنة حسب عمر الطفل — د. سارة النحّاس" },
      {
        name: "description",
        content: "مقالات طويلة بلغة الأمّ في التربية وعلم نفس الطفل: مواقف يومية، ما الذي يشعر به طفلك، وعبارات جاهزة تستخدمها اليوم.",
      },
      { property: "og:title", content: "المدوّنة حسب عمر الطفل — د. سارة النحّاس" },
      { property: "og:description", content: "مقالات طويلة في التربية وعلم نفس الطفل، مكتوبة لأمّ متعبة في الحادية عشرة ليلاً." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "ar_SY" },
      { property: "og:site_name", content: "د. سارة النحّاس" },
      ...ogImageMeta(),
      ...canonicalMeta("/blog"),
    ],
    links: [canonicalLink("/blog")],
  }),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(postsQ()),
      context.queryClient.ensureQueryData(statsQ()),
      context.queryClient.ensureQueryData(servicesQ()),
    ]);
  },
  errorComponent: ({ error }) => <p className="mx-auto max-w-3xl p-10 text-text-secondary">{String(error)}</p>,
  notFoundComponent: RouteNotFound,
  component: Page,
});

function Page() {
  const { rakiza } = Route.useSearch();
  const navigate = useNavigate({ from: "/blog/" });
  const { data } = useSuspenseQuery(postsQ());
  const { data: stats } = useSuspenseQuery(statsQ());
  const { data: services } = useSuspenseQuery(servicesQ());
  const { reader, isSaved, isRead, toggleSave, setAge } = useReader();
  const [showAll, setShowAll] = useState(false);

  const posts = data.rows as unknown as BlogPost[];
  const age = reader.age;
  const pillar = age ? null : pillarById(rakiza ?? "");
  const prices = consultPrices(services as any[]);

  const filtered = useMemo(
    () => (pillar ? posts.filter((p) => postInPillar(p.rakiza, pillar.id)) : posts),
    [posts, pillar],
  );
  const path = useMemo(() => (age ? orderPath(age, posts) : []), [age, posts]);
  const visible = showAll ? filtered : filtered.slice(0, 6);
  const total = stats.count ?? posts.length;
  const minutes =
    stats.minReading && stats.maxReading ? `${stats.minReading}–${stats.maxReading} دقيقة` : null;

  const pickAge = (id: AgeChipId) => void setAge(age === id ? null : id);
  const pickPillar = (id: string) => {
    setShowAll(false);
    navigate({ search: (prev) => ({ ...prev, rakiza: id === "all" || id === rakiza ? undefined : id }), replace: true });
  };

  const chip = "inline-flex min-h-11 shrink-0 snap-start items-center rounded-ds-pill px-4 font-display text-[14px] font-bold transition-colors duration-ds-fast";
  const chipOn = "bg-amber-500 text-brown-700";
  const chipOff = "border border-hairline-default bg-surface text-brown-700 hover:bg-cream-100";

  return (
    <div>
      <Section tone="white" edge="hairline" inner="px-5 pb-10 pt-8 lg:mx-auto lg:max-w-[52rem] lg:pb-12">
        <p className={eyebrow}>{pillar ? "المدوّنة · الركيزة" : `المدوّنة · ${articlesPhrase(total)}`}</p>
        <h1 className="mt-2 max-w-[44rem] font-display text-[42px] font-extrabold leading-[1.22] text-text-primary">
          {pillar ? (
            pillar.label
          ) : (
            <>
              القراءة حسب <span className="mark">عمر طفلك</span>
            </>
          )}
        </h1>
        {!pillar && (
          <p className="mt-4 max-w-[44rem] text-[19px] leading-[1.8] text-text-secondary">
            مقالات طويلة شوي{minutes ? ` (${minutes})` : ""}، مكتوبة لأهل بآخر يوم طويل. كل مقال بيبلّش بمشهد حقيقي، مش بتعريف.
          </p>
        )}

        <div className="mt-6 rounded-ds-xl bg-cream-50 p-5">
          <h2 className="font-display text-[19px] font-extrabold text-text-primary">عمر طفلك</h2>
          <div role="group" aria-label="عمر طفلك" className="-mx-1 mt-3 flex snap-x snap-mandatory scroll-px-1 gap-2 overflow-x-auto no-scrollbar px-1 pb-1">
            {AGE_CHIPS.map((c) => (
              <button
                key={c.id}
                type="button"
                aria-pressed={age === c.id}
                onClick={() => pickAge(c.id)}
                className={`${chip} ${age === c.id ? chipOn : chipOff}`}
              >
                <bdi dir="ltr">{c.label}</bdi>
              </button>
            ))}
          </div>
          <p className="mt-3 text-[14px] leading-[1.7] text-text-secondary">
            بعد الاختيار بتترتّب المقالات كمسار قراءة، وبيبيّن المقروء منها.
          </p>
          {age && (
            <PathProgress label={chipById(age).label} read={path.filter((p) => isRead(p.id)).length} total={path.length} />
          )}
        </div>
      </Section>

      <Section tone="cream" className="pb-14 pt-8 lg:pt-10">
      {age ? (
        <div className="px-5 lg:mx-auto lg:max-w-[52rem]">
          <PathView path={path} isRead={isRead} />
          <section className="reveal mx-[-8px] mt-10 rounded-ds-2xl bg-maroon-100 px-6 py-8">
            <h2 className="font-display text-[26px] font-extrabold text-text-primary">المسار بيضل وين وقفت</h2>
            <p className="mt-3 text-[17px] leading-[1.85] text-text-secondary">
              المقروء والمحفوظ بيتسجّلوا بلوحتي، والرجوع للمسار ممكن من أي جهاز. بدون حساب بيضلّوا على هذا الجهاز.
            </p>
            {reader.mode === "device" && (
              <Link to="/auth" className={`${primaryBtn} mt-5 w-full`}>
                حفظ المسار بحساب مجاني
              </Link>
            )}
          </section>
        </div>
      ) : (
        <div className="px-5 lg:mx-auto lg:max-w-[76rem]">
          <p className={eyebrow}>الركيزة</p>
          <div role="group" aria-label="الركيزة" className="-mx-1 mt-3 flex snap-x snap-mandatory scroll-px-1 gap-2 overflow-x-auto no-scrollbar px-1 pb-1">
            <button type="button" aria-pressed={!pillar} onClick={() => pickPillar("all")} className={`${chip} ${!pillar ? chipOn : chipOff}`}>
              الكل
            </button>
            {PILLARS.map((p) => (
              <button
                key={p.id}
                type="button"
                aria-pressed={pillar?.id === p.id}
                onClick={() => pickPillar(p.id)}
                className={`${chip} ${pillar?.id === p.id ? chipOn : chipOff}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <p className="mt-6 flex flex-wrap items-baseline gap-2">
            <span className="font-display text-[30px] font-extrabold text-text-primary">{filtered.length}</span>
            <span className="text-[15px] text-text-secondary">
              {pillar
                ? `${articlesPhrase(filtered.length).replace(/^\d+\s/, "")} · السحب الأفقي بيتنقّل بينها من داخل أي مقال`
                : `${articlesPhrase(filtered.length).replace(/^\d+\s/, "")} · الأحدث أوّلاً`}
            </span>
          </p>

          {filtered.length === 0 ? (
            <div className="mt-6 rounded-ds-xl bg-cream-50 p-8 text-center">
              <p className="text-text-secondary">ما في مقالات بهالركيزة لهلّق.</p>
              <button type="button" className={`${secondaryBtn} mt-4`} onClick={() => pickPillar("all")}>
                عرض كل المقالات
              </button>
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
              {visible.map((p) => (
                <PostCard key={p.id} post={p} saved={isSaved(p.id)} onToggleSave={() => void toggleSave(p.id)} />
              ))}
            </div>
          )}
          {!showAll && filtered.length > 6 && (
            <button type="button" className={`${secondaryBtn} mt-5 w-full lg:mx-auto lg:flex lg:w-auto`} onClick={() => setShowAll(true)}>
              عرض {articlesPhrase(filtered.length - 6)} غيرهم
            </button>
          )}
        </div>
      )}
      </Section>

      <Section tone="white" className="pb-14">
      <div>
      <BrownCtaBand
        heading="ما في مقال عن موقفك بالذات؟"
        line={`${prices.duration ?? 45} دقيقة مع د. سارة عن بيتكم بالذات. اليوم والساعة بنتفق عليهم على واتساب.`}
        price={prices.price}
        packagePrice={prices.packagePrice}
      />
      </div>
      </Section>
    </div>
  );
}
